import { cva, type VariantProps } from "class-variance-authority";
import type { ButtonHTMLAttributes, ReactNode } from "react";
import { cn } from "../../utils/cn";

const buttonStyles = cva(
    "inline-flex items-center justify-center font-medium transition-all duration-200 whitespace-nowrap disabled:opacity-60 disabled:pointer-events-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-2",
    {
        variants: {
            variant: {
                primary: "bg-brand-600 text-white shadow-soft hover:bg-brand-700 hover:shadow-medium active:bg-brand-800",
                secondary: "bg-gray-100 text-gray-900 shadow-soft hover:bg-gray-200 hover:shadow-medium active:bg-gray-300",
                outline: "border-2 border-brand-600 text-brand-700 hover:bg-brand-50 active:bg-brand-100",
                ghost: "text-gray-700 hover:bg-gray-100 active:bg-gray-200",
                danger: "bg-error-600 text-white shadow-soft hover:bg-error-700 hover:shadow-medium active:bg-error-800",
                success: "bg-success-600 text-white shadow-soft hover:bg-success-700 hover:shadow-medium active:bg-success-800",
            },
            size: {
                sm: "h-9 px-3 text-sm rounded-lg",
                md: "h-11 px-4 text-base rounded-lg",
                lg: "h-12 px-6 text-lg rounded-xl",
            },
            fullWidth: {
                true: "w-full",
            },
        },
        defaultVariants: {
            variant: "primary",
            size: "md",
        },
    }
);

export interface ButtonProps
    extends ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonStyles> {
    children: ReactNode;
    loading?: boolean;
    leftIcon?: ReactNode;
    rightIcon?: ReactNode;
}

function ButtonSpinner() {
    return (
        <svg className="w-4 h-4 mr-2 animate-spin" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            />
        </svg>
    );
}

export default function Button({
    children,
    variant,
    size,
    fullWidth,
    loading = false,
    leftIcon,
    rightIcon,
    className,
    disabled,
    ...rest
}: ButtonProps) {
    const isDisabled = disabled || loading;

    return (
        <button
            className={cn(buttonStyles({ variant, size, fullWidth }), className)}
            disabled={isDisabled}
            {...rest}
        >
            {loading ? (
                <ButtonSpinner />
            ) : (
                leftIcon && <span className="mr-2">{leftIcon}</span>
            )}
            {children}
            {!loading && rightIcon && <span className="ml-2">{rightIcon}</span>}
        </button>
    );
}
